import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  Trophy,
  ExternalLink,
  Package,
  Copy,
  Check,
  BarChart3,
  List,
  FileCode,
  Globe,
  Camera,
  ChevronDown,
  Link2,
  Star,
} from 'lucide-react';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { cn } from '../utils/cn';
import { apogeeApiService } from '../services/apogeeApi';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { PackStatsCards } from '../components/apogee/PackStatsCards';
import { PackTimeseriesChart } from '../components/apogee/PackTimeseriesChart';
import { ModlistDiffViewer } from '../components/apogee/ModlistDiffViewer';
import { ChangelogViewer } from '../components/apogee/ChangelogViewer';
import { ExternalIdBadge } from '../components/apogee/ExternalIdBadge';
import { PackCrossModuleSection } from '../components/apogee/PackCrossModuleSection';
import { PackServerInfo } from '../components/apogee/PackServerInfo';
import { PackEnrichmentSection } from '../components/apogee/PackEnrichmentSection';
import { CreatePartnershipModal } from '../components/partnerships/CreatePartnershipModal';
import { CreateModpackNoteModal } from '../components/CreateModpackNoteModal';
import {
  PackRow,
  SnapshotRow,
  PackTimeseriesPoint,
  ModlistRow,
  ModlistDiffResponse,
  ExternalIdRow,
  CurseForgeFileRow,
  ChangelogRow,
  TimeseriesBucket,
  CF_RELEASE_TYPES,
  PackEnrichmentResponse,
  CurseForgeFeaturedSpanRow,
  CurseForgeFileMetricPointRow,
} from '../types/apogee';
import { enrichmentAuthorCandidates, enrichmentFromPackRow } from '../utils/apogeeEnrichment';

// ── Types ───────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'timeseries' | 'modlists' | 'curseforge' | 'external' | 'snapshots' | 'integrations';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
  { id: 'timeseries', label: 'Timeseries', icon: BarChart3 },
  { id: 'modlists', label: 'Mod Lists', icon: List },
  { id: 'curseforge', label: 'CurseForge', icon: FileCode },
  { id: 'external', label: 'External IDs', icon: Globe },
  { id: 'snapshots', label: 'Snapshots', icon: Camera },
];

const BUCKET_OPTIONS: { value: TimeseriesBucket; label: string }[] = [
  { value: 'raw', label: 'Raw' },
  { value: '1h', label: '1 Hour' },
  { value: '1d', label: '1 Day' },
];

const PRESET_RANGES = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

const formatNumber = (n: number | undefined | null): string =>
  n == null || Number.isNaN(n) ? '—' : n.toLocaleString();

// ── Virtualized Snapshots Tab ───────────────────────────────────────────────

const SNAPSHOT_HEAD = 'px-3 py-2 text-[11px] font-medium uppercase tracking-[0.08em] text-glass-400 shrink-0';

const SnapshotsTab = ({ snapshots, loading }: { snapshots: SnapshotRow[]; loading: boolean }) => {
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  // Separate scroll roots: one ref on both DOM nodes would point at the last one (mobile),
  // which is display:none on md+ — the virtualizer then sees zero height and renders no rows.
  const desktopVirtualizer = useVirtualizer({
    count: snapshots.length,
    getScrollElement: () => desktopScrollRef.current,
    estimateSize: () => 40,
  });
  const mobileVirtualizer = useVirtualizer({
    count: snapshots.length,
    getScrollElement: () => mobileScrollRef.current,
    estimateSize: () => 40,
  });

  if (loading) {
    return (
      <div className="glass-card p-4 md:p-5" aria-busy="true">
        <SectionHeader title="All Snapshots" icon={Camera} />
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => <SkeletonLoader key={i} className="h-8 w-full" />)}
        </div>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="All Snapshots" icon={Camera} />
        <EmptyState
          bare
          compact
          icon={Camera}
          title="No snapshot data available"
          description="Snapshots are recorded as Apogee observes this pack."
        />
      </div>
    );
  }

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="All Snapshots"
        icon={Camera}
        count={snapshots.length}
        description={`${snapshots.length.toLocaleString()} snapshots loaded`}
      />

      {/* Desktop — virtualized */}
      <div className="hidden md:block subcard overflow-x-auto">
        {/* Header */}
        <div className="flex border-b border-glass-200/10 min-w-[840px]">
          <div className={cn(SNAPSHOT_HEAD, 'w-[180px]')}>Observed At</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[120px] text-right')}>Downloads</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[80px] text-right')}>Rank</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[120px]')}>Latest Release</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[120px]')}>Last Modified</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[120px]')}>Last Updated</div>
          <div className={cn(SNAPSHOT_HEAD, 'w-[100px]')}>Source</div>
        </div>
        {/* Virtualized rows */}
        <div
          ref={desktopScrollRef}
          className="h-[500px] overflow-y-auto min-w-[840px]"
        >
          <div style={{ height: `${desktopVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {desktopVirtualizer.getVirtualItems().map((virtualRow) => {
              const snap = snapshots[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  className="flex border-b border-glass-200/10 hover:bg-glass-200/5 absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)`, height: `${virtualRow.size}px` }}
                >
                  <div className="px-3 py-2 w-[180px] shrink-0 text-xs font-mono text-glass-300 tabular-nums flex items-center">
                    {format(new Date(snap.observed_at), 'yyyy-MM-dd HH:mm')}
                  </div>
                  <div className="px-3 py-2 w-[120px] shrink-0 text-sm text-glass-300 tabular-nums text-right flex items-center justify-end">
                    {snap.download_count != null ? formatNumber(snap.download_count) : '—'}
                  </div>
                  <div className="px-3 py-2 w-[80px] shrink-0 text-sm text-glass-400 tabular-nums text-right flex items-center justify-end">
                    {snap.popularity_rank !== null ? `#${snap.popularity_rank}` : '—'}
                  </div>
                  <div className="px-3 py-2 w-[120px] shrink-0 text-xs text-glass-400 tabular-nums flex items-center">
                    {snap.latest_release_date ? format(new Date(snap.latest_release_date), 'MMM d, yyyy') : '—'}
                  </div>
                  <div className="px-3 py-2 w-[120px] shrink-0 text-xs text-glass-400 tabular-nums flex items-center">
                    {snap.last_modified ? format(new Date(snap.last_modified), 'MMM d, yyyy') : '—'}
                  </div>
                  <div className="px-3 py-2 w-[120px] shrink-0 text-xs text-glass-400 tabular-nums flex items-center">
                    {snap.last_updated ? format(new Date(snap.last_updated), 'MMM d, yyyy') : '—'}
                  </div>
                  <div className="px-3 py-2 w-[100px] shrink-0 flex items-center">
                    <span className="badge badge-neutral">{snap.source}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile — virtualized */}
      <div className="md:hidden">
        <div
          ref={mobileScrollRef}
          className="h-[400px] overflow-y-auto"
        >
          <div style={{ height: `${mobileVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {mobileVirtualizer.getVirtualItems().map((virtualRow) => {
              const snap = snapshots[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  className="subcard p-3 space-y-1 absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)`, height: `${virtualRow.size}px` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-glass-300 tabular-nums">{format(new Date(snap.observed_at), 'MMM d, HH:mm')}</span>
                    <span className="badge badge-neutral">{snap.source}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm tabular-nums">
                    <span className="text-glass-300">
                      {snap.download_count != null ? `${formatNumber(snap.download_count)} downloads` : '—'}
                    </span>
                    {snap.popularity_rank !== null && (
                      <span className="text-glass-400">#{snap.popularity_rank}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main View ───────────────────────────────────────────────────────────────

export const ModpackDetailView = () => {
  // Route can be `/modpacks/:mpiId` (MPI-matched) or `/modpacks/id/:apogeeId`
  // (stable internal id — required for CF-primary packs that have no `mpi_id`).
  const { mpiId: mpiIdParam, apogeeId: apogeeIdParam } = useParams<{ mpiId?: string; apogeeId?: string }>();
  const navigate = useNavigate();

  const routeMode: 'mpi' | 'apogee' = apogeeIdParam != null ? 'apogee' : 'mpi';
  const routeMpiId = useMemo(() => (mpiIdParam != null ? Number(mpiIdParam) : NaN), [mpiIdParam]);
  const routeApogeeId = useMemo(() => (apogeeIdParam != null ? Number(apogeeIdParam) : NaN), [apogeeIdParam]);

  // ── Pack data ─────────────────────────────────────────────────────────
  const [pack, setPack] = useState<PackRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Tab ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // ── Overview tab data ─────────────────────────────────────────────────
  const [overviewTimeseries, setOverviewTimeseries] = useState<PackTimeseriesPoint[]>([]);
  const [overviewTsLoading, setOverviewTsLoading] = useState(false);
  const [recentSnapshots, setRecentSnapshots] = useState<SnapshotRow[]>([]);
  const [recentSnapshotsLoading, setRecentSnapshotsLoading] = useState(false);

  // ── Timeseries tab data ───────────────────────────────────────────────
  const [tsData, setTsData] = useState<PackTimeseriesPoint[]>([]);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsBucket, setTsBucket] = useState<TimeseriesBucket>('1d');
  const [tsFrom, setTsFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [tsTo, setTsTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  // ── Modlists tab data ─────────────────────────────────────────────────
  const [modlists, setModlists] = useState<ModlistRow[]>([]);
  const [modlistsLoading, setModlistsLoading] = useState(false);
  const [expandedModlist, setExpandedModlist] = useState<number | null>(null);
  const [diffMode, setDiffMode] = useState<'snapshot' | 'date'>('snapshot');
  const [diffFromId, setDiffFromId] = useState<number | null>(null);
  const [diffToId, setDiffToId] = useState<number | null>(null);
  const [diffFromDate, setDiffFromDate] = useState('');
  const [diffToDate, setDiffToDate] = useState('');
  const [diff, setDiff] = useState<ModlistDiffResponse | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // ── CurseForge tab data ───────────────────────────────────────────────
  const [cfFiles, setCfFiles] = useState<CurseForgeFileRow[]>([]);
  const [cfFilesLoading, setCfFilesLoading] = useState(false);
  const [changelogs, setChangelogs] = useState<ChangelogRow[]>([]);
  const [changelogsLoading, setChangelogsLoading] = useState(false);
  const [changelogsFull, setChangelogsFull] = useState(false);
  const [cfFeaturedSpans, setCfFeaturedSpans] = useState<CurseForgeFeaturedSpanRow[]>([]);
  const [cfFeaturedLatestCrawl, setCfFeaturedLatestCrawl] = useState<string | null>(null);
  const [cfFeaturedLoading, setCfFeaturedLoading] = useState(false);
  const [cfMetricPoints, setCfMetricPoints] = useState<CurseForgeFileMetricPointRow[]>([]);
  const [cfMetricLoading, setCfMetricLoading] = useState(false);

  // ── External IDs tab data ─────────────────────────────────────────────
  const [externalIds, setExternalIds] = useState<ExternalIdRow[]>([]);
  const [externalIdsLoading, setExternalIdsLoading] = useState(false);

  // ── Snapshots tab data ────────────────────────────────────────────────
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);

  // ── Copy state ────────────────────────────────────────────────────────
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // ── Cross-module modals ───────────────────────────────────────────────
  const [isCreatePartnershipOpen, setIsCreatePartnershipOpen] = useState(false);
  const [isCreateModpackNoteOpen, setIsCreateModpackNoteOpen] = useState(false);

  // ── Pack enrichment (Modrinth / CurseForge) ───────────────────────────
  const [enrichment, setEnrichment] = useState<PackEnrichmentResponse | null>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);

  // ── Set document title ────────────────────────────────────────────────
  useDocumentTitle(pack ? `${pack.name} — Modpack Browser` : 'Modpack Browser');

  // ── Effective ids (derived from the loaded pack) ──────────────────────
  // `apogee_id` is always present; `mpi_id` is nullable under CF-primary identity.
  // Apogee-id endpoint variants work for every pack; MPI-only endpoints need a match.
  const apogeeId = pack?.apogee_id ?? null;
  const effectiveMpiId = pack?.mpi_id ?? null;

  // ── Fetch pack ────────────────────────────────────────────────────────
  const fetchPack = useCallback(async () => {
    const validMpi = routeMode === 'mpi' && Number.isFinite(routeMpiId);
    const validApogee = routeMode === 'apogee' && Number.isFinite(routeApogeeId);
    if (!validMpi && !validApogee) {
      setError('Invalid pack ID');
      setPack(null);
      setLoading(false);
      setEnrichment(null);
      setEnrichmentLoading(false);
      setEnrichmentError(null);
      return;
    }
    setLoading(true);
    setError(null);
    setEnrichment(null);
    setEnrichmentError(null);
    setEnrichmentLoading(true);
    try {
      const data = routeMode === 'apogee'
        ? await apogeeApiService.getPackByApogeeId(routeApogeeId)
        : await apogeeApiService.getPackByMpiId(routeMpiId);
      setPack(data);
      const embedded = enrichmentFromPackRow(data);
      if (embedded) {
        setEnrichment(embedded);
        setEnrichmentError(null);
        setEnrichmentLoading(false);
      } else {
        try {
          // Apogee-id enrichment route resolves the same pack regardless of MPI match.
          const en = await apogeeApiService.getPackEnrichmentByApogeeId(data.apogee_id);
          setEnrichment(en);
          setEnrichmentError(null);
        } catch (err) {
          setEnrichment(null);
          setEnrichmentError(err instanceof Error ? err.message : 'Failed to load enrichment');
        } finally {
          setEnrichmentLoading(false);
        }
      }
    } catch (err) {
      setPack(null);
      setError(err instanceof Error ? err.message : 'Failed to load pack');
      setEnrichment(null);
      setEnrichmentLoading(false);
      setEnrichmentError(null);
    } finally {
      setLoading(false);
    }
  }, [routeMode, routeMpiId, routeApogeeId]);

  useEffect(() => { fetchPack(); }, [fetchPack]);

  const partnershipAuthorCandidates = useMemo(
    () => enrichmentAuthorCandidates(enrichment),
    [enrichment],
  );

  // ── Fetch overview data ───────────────────────────────────────────────
  useEffect(() => {
    if (!pack || activeTab !== 'overview') return;
    // Mini timeseries — last 30 days (apogee-id route works for all packs)
    if (apogeeId != null) {
      setOverviewTsLoading(true);
      const now = new Date();
      apogeeApiService.getPackTimeseriesByApogeeId(apogeeId, {
        from: subDays(now, 30).toISOString(),
        to: now.toISOString(),
        bucket: '1d',
      })
        .then((res) => setOverviewTimeseries(res.points || []))
        .catch(() => {})
        .finally(() => setOverviewTsLoading(false));
    } else {
      setOverviewTimeseries([]);
    }

    // Recent snapshots (MPI-only endpoint)
    if (effectiveMpiId != null) {
      setRecentSnapshotsLoading(true);
      apogeeApiService.getPackSnapshots(effectiveMpiId, 5)
        .then((res) => setRecentSnapshots(res.items || []))
        .catch(() => {})
        .finally(() => setRecentSnapshotsLoading(false));
    } else {
      setRecentSnapshots([]);
    }
  }, [pack, activeTab, apogeeId, effectiveMpiId]);

  // ── Fetch timeseries tab data ─────────────────────────────────────────
  const fetchTimeseries = useCallback(async () => {
    if (activeTab !== 'timeseries') return;
    if (apogeeId == null) {
      setTsData([]);
      return;
    }
    setTsLoading(true);
    try {
      const res = await apogeeApiService.getPackTimeseriesByApogeeId(apogeeId, {
        from: new Date(tsFrom).toISOString(),
        to: new Date(tsTo + 'T23:59:59Z').toISOString(),
        bucket: tsBucket,
      });
      setTsData(res.points || []);
    } catch {
      setTsData([]);
    } finally {
      setTsLoading(false);
    }
  }, [activeTab, apogeeId, tsFrom, tsTo, tsBucket]);

  useEffect(() => { fetchTimeseries(); }, [fetchTimeseries]);

  // ── Fetch modlists (MPI-only endpoint) ────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'modlists') return;
    if (effectiveMpiId == null) {
      setModlists([]);
      return;
    }
    setModlistsLoading(true);
    apogeeApiService.getPackModlists(effectiveMpiId, 50)
      .then((res) => setModlists(res.items || []))
      .catch(() => {})
      .finally(() => setModlistsLoading(false));
  }, [activeTab, effectiveMpiId]);

  // ── Fetch CurseForge data ─────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'curseforge') return;

    // Files & changelogs are MPI-keyed endpoints.
    if (effectiveMpiId != null) {
      setCfFilesLoading(true);
      apogeeApiService.getPackCurseForgeFiles(effectiveMpiId, 100)
        .then((res) => setCfFiles(res.items || []))
        .catch(() => {})
        .finally(() => setCfFilesLoading(false));

      setChangelogsLoading(true);
      apogeeApiService.getPackCurseForgeChangelogs(effectiveMpiId, { limit: 50, full: changelogsFull })
        .then((res) => setChangelogs(res.items || []))
        .catch(() => {})
        .finally(() => setChangelogsLoading(false));
    } else {
      setCfFiles([]);
      setChangelogs([]);
    }

    // Featured history & file-metric-points have apogee-id variants (work for all packs).
    if (apogeeId != null) {
      setCfFeaturedLoading(true);
      apogeeApiService.getPackCurseForgeFeaturedHistoryByApogeeId(apogeeId)
        .then((res) => {
          setCfFeaturedSpans(res.spans || []);
          setCfFeaturedLatestCrawl(res.latest_crawl_observed_at ?? null);
        })
        .catch(() => {
          setCfFeaturedSpans([]);
          setCfFeaturedLatestCrawl(null);
        })
        .finally(() => setCfFeaturedLoading(false));

      setCfMetricLoading(true);
      apogeeApiService.getPackCurseForgeFileMetricPointsByApogeeId(apogeeId, { limit: 2000 })
        .then((res) => setCfMetricPoints(res.items || []))
        .catch(() => setCfMetricPoints([]))
        .finally(() => setCfMetricLoading(false));
    } else {
      setCfFeaturedSpans([]);
      setCfFeaturedLatestCrawl(null);
      setCfMetricPoints([]);
    }
  }, [activeTab, apogeeId, effectiveMpiId, changelogsFull]);

  // ── Fetch external IDs (MPI-only endpoint) ────────────────────────────
  useEffect(() => {
    if (activeTab !== 'external') return;
    if (effectiveMpiId == null) {
      setExternalIds([]);
      return;
    }
    setExternalIdsLoading(true);
    apogeeApiService.getPackExternalIds(effectiveMpiId)
      .then((res) => setExternalIds(res.items || []))
      .catch(() => {})
      .finally(() => setExternalIdsLoading(false));
  }, [activeTab, effectiveMpiId]);

  // ── Fetch all snapshots (MPI-only endpoint) ───────────────────────────
  useEffect(() => {
    if (activeTab !== 'snapshots') return;
    if (effectiveMpiId == null) {
      setSnapshots([]);
      return;
    }
    setSnapshotsLoading(true);
    apogeeApiService.getPackSnapshots(effectiveMpiId, 500)
      .then((res) => setSnapshots(res.items || []))
      .catch(() => {})
      .finally(() => setSnapshotsLoading(false));
  }, [activeTab, effectiveMpiId]);

  // ── Compute diff ──────────────────────────────────────────────────────
  const computeDiff = async () => {
    if (effectiveMpiId == null) return;
    setDiffLoading(true);
    setDiff(null);
    try {
      if (diffMode === 'snapshot') {
        if (diffFromId === null || diffToId === null) return;
        const res = await apogeeApiService.getPackModlistDiff(effectiveMpiId, {
          from_snapshot_id: diffFromId,
          to_snapshot_id: diffToId,
        });
        setDiff(res);
      } else {
        if (!diffFromDate || !diffToDate) return;
        const res = await apogeeApiService.getPackModlistDiff(effectiveMpiId, {
          from_observed_at: new Date(diffFromDate).toISOString(),
          to_observed_at: new Date(diffToDate + 'T23:59:59Z').toISOString(),
        });
        setDiff(res);
      }
    } catch {
      // ignore
    } finally {
      setDiffLoading(false);
    }
  };

  const canComputeDiff = diffMode === 'snapshot'
    ? diffFromId !== null && diffToId !== null
    : diffFromDate !== '' && diffToDate !== '';

  // Shown on MPI-only tabs for CurseForge-primary packs that have no Modpack Index match.
  const mpiOnlyNote = effectiveMpiId == null ? (
    <div className="alert alert-warning" role="note">
      <AlertCircle />
      <span>
        This pack is tracked via CurseForge only (no Modpack Index match), so snapshots, mod lists,
        external IDs, and CurseForge file/changelog history are unavailable. Downloads, popularity,
        timeseries, enrichment, and featured-carousel data are still shown.
      </span>
    </div>
  ) : null;

  // ── Copy helper ───────────────────────────────────────────────────────
  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // ── Preset range handler ──────────────────────────────────────────────
  const handlePresetRange = (days: number) => {
    const now = new Date();
    setTsFrom(format(subDays(now, days), 'yyyy-MM-dd'));
    setTsTo(format(now, 'yyyy-MM-dd'));
    if (days <= 1) setTsBucket('raw');
    else if (days <= 7) setTsBucket('1h');
    else setTsBucket('1d');
  };

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => navigate('/modpacks')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-glass-400 hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Modpack Browser
          </button>
          <SkeletonLoader className="h-3 w-20" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="w-12 h-12 md:w-14 md:h-14 rounded-lg shrink-0" />
            <SkeletonLoader className="h-7 w-64 max-w-full" />
            <SkeletonLoader className="h-5 w-14 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-full max-w-xl" />
          <div className="flex gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-5 w-24 rounded-full" />
            ))}
          </div>
        </div>
        <div className="flex gap-4 border-b border-glass-200/10 pb-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonLoader key={i} className="h-4 w-16" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-2">
              <SkeletonLoader className="h-3 w-20" />
              <SkeletonLoader className="h-7 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error || !pack) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Planning"
          title="Modpack"
          backTo="/modpacks"
          backLabel="Modpack Browser"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load pack"
          description={error || 'Pack not found'}
          action={
            <>
              <button onClick={fetchPack} className="glass-button">Retry</button>
              <button onClick={() => navigate('/modpacks')} className="glass-button-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Browser
              </button>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pack Header */}
      <PageHeader
        eyebrow="Planning"
        backTo="/modpacks"
        backLabel="Modpack Browser"
        title={
          <span className="inline-flex items-center gap-3 max-w-full">
            <span className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden bg-dark-900/50 ring-1 ring-glass-200/15 shrink-0 flex items-center justify-center">
              {pack.thumbnail_url ? (
                <img src={pack.thumbnail_url} alt={pack.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-6 h-6 text-glass-400" />
              )}
            </span>
            <span className="min-w-0 truncate">{pack.name}</span>
          </span>
        }
        titleAdornment={
          typeof pack.popularity_rank === 'number' ? (
            <span className="badge badge-accent" title="Popularity rank">
              <Trophy className="w-3 h-3" />
              #{pack.popularity_rank}
            </span>
          ) : undefined
        }
        meta={<span className="font-mono text-xs text-glass-400 truncate max-w-[16rem]">{pack.slug}</span>}
        description={pack.summary || undefined}
        actions={
          <button
            onClick={() => setActiveTab('integrations')}
            className="glass-button"
          >
            <Link2 className="w-4 h-4" />
            Notes, Partnerships &amp; Build Links
          </button>
        }
      >
        {/* ID badges + external links */}
        <div className="flex flex-wrap items-center gap-1.5">
          {pack.mpi_id != null && (
            <button
              type="button"
              onClick={() => copyToClipboard(String(pack.mpi_id), 'mpi')}
              className="badge badge-neutral font-mono hover:border-glass-200/30 hover:text-white transition-colors"
              title="Copy MPI id"
            >
              <span>MPI: {pack.mpi_id}</span>
              {copiedField === 'mpi' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 opacity-60" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => copyToClipboard(String(pack.apogee_id), 'apogee')}
            className="badge badge-neutral font-mono hover:border-glass-200/30 hover:text-white transition-colors"
            title="Copy Apogee id"
          >
            <span>Apogee: {pack.apogee_id}</span>
            {copiedField === 'apogee' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 opacity-60" />}
          </button>
          {pack.cf_mod_id != null && (
            <button
              type="button"
              onClick={() => copyToClipboard(String(pack.cf_mod_id), 'cf')}
              className="badge badge-orange font-mono hover:brightness-125 transition-[filter]"
              title="Copy CurseForge mod id"
            >
              <span>CF mod: {pack.cf_mod_id}</span>
              {copiedField === 'cf' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 opacity-60" />}
            </button>
          )}
          {(pack.mpi_id != null || pack.modpack_index_page_url) && (
            <a
              href={pack.modpack_index_page_url || `https://www.modpackindex.com/modpack/${pack.mpi_id}/${pack.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="badge badge-info hover:brightness-125 transition-[filter]"
            >
              <span>Modpack Index</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {pack.external_page_url && (
            <a
              href={pack.external_page_url}
              target="_blank"
              rel="noopener noreferrer"
              className="badge badge-orange hover:brightness-125 transition-[filter]"
            >
              <span>External Page</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {pack.links && Object.entries(pack.links).map(([key, url]) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="badge badge-purple capitalize hover:brightness-125 transition-[filter]"
            >
              <span>{key}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>

        {/* Key metrics (desktop) */}
        {(pack.download_count != null || pack.first_release_at) && (
          <div className="hidden md:flex flex-wrap items-center gap-x-6 gap-y-2">
            {pack.download_count != null && (
              <div>
                <div className="eyebrow">Downloads</div>
                <div className="text-sm font-semibold text-white tabular-nums">{formatNumber(pack.download_count)}</div>
              </div>
            )}
            {pack.download_count != null && pack.first_seen_download_count > 0 && (
              <div>
                <div className="eyebrow">Since first seen</div>
                <div className="text-sm font-medium text-glass-300 tabular-nums">+{formatNumber(pack.download_count - pack.first_seen_download_count)}</div>
              </div>
            )}
            {pack.first_release_at && (
              <div>
                <div className="eyebrow">First release</div>
                <div className="text-sm font-medium text-glass-300 tabular-nums">{format(new Date(pack.first_release_at), 'MMM d, yyyy')}</div>
              </div>
            )}
          </div>
        )}
      </PageHeader>

      {/* Tab Bar */}
      <div className="tabs" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="tab"
              data-active={activeTab === tab.id ? 'true' : 'false'}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Overview Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <PackStatsCards pack={pack} />

          <PackEnrichmentSection
            enrichment={enrichment}
            loading={enrichmentLoading}
            error={enrichmentError}
          />

          {/* Mini timeseries chart */}
          <PackTimeseriesChart
            data={overviewTimeseries}
            isLoading={overviewTsLoading}
            title="Downloads — Last 30 Days"
            height={250}
          />

          {/* CDU Servers running this pack (server list maps by MPI id) */}
          {effectiveMpiId != null && <PackServerInfo mpiId={effectiveMpiId} />}

          {/* Recent snapshots */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader title="Recent Snapshots" icon={Camera} />
            {recentSnapshotsLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-10 w-full" />)}
              </div>
            ) : recentSnapshots.length === 0 ? (
              <EmptyState bare compact icon={Camera} title="No snapshots available" />
            ) : (
              <div className="divide-y divide-glass-200/10">
                {recentSnapshots.map((snap, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-glass-400 tabular-nums">
                        {format(new Date(snap.observed_at), 'MMM d, HH:mm')}
                      </span>
                      <span className="badge badge-neutral">
                        {snap.source}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm tabular-nums">
                      <span className="text-glass-300">
                        {snap.download_count != null ? `${formatNumber(snap.download_count)} downloads` : '—'}
                      </span>
                      {snap.popularity_rank !== null && (
                        <span className="text-glass-400">#{snap.popularity_rank}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cross-module integrations preview */}
          {pack && (
            <PackCrossModuleSection
              packName={pack.name}
              packSlug={pack.slug}
              mpiId={effectiveMpiId}
              onCreatePartnership={() => setIsCreatePartnershipOpen(true)}
              onCreateModpackNote={() => setIsCreateModpackNoteOpen(true)}
            />
          )}
        </div>
      )}

      {/* ── Timeseries Tab ───────────────────────────────────────────────── */}
      {activeTab === 'timeseries' && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2 flex-wrap">
            {/* Presets */}
            <div className="flex items-center gap-1 flex-wrap">
              {PRESET_RANGES.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetRange(preset.days)}
                  className="glass-button-sm"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Date range */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="date"
                className="glass-input min-w-0"
                value={tsFrom}
                onChange={(e) => setTsFrom(e.target.value)}
                aria-label="From date"
              />
              <span className="text-xs text-glass-400">to</span>
              <input
                type="date"
                className="glass-input min-w-0"
                value={tsTo}
                onChange={(e) => setTsTo(e.target.value)}
                aria-label="To date"
              />
            </div>

            {/* Bucket */}
            <div className="segmented self-start" role="group" aria-label="Bucket">
              {BUCKET_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTsBucket(opt.value)}
                  className="segmented-item"
                  data-active={tsBucket === opt.value ? 'true' : 'false'}
                  aria-pressed={tsBucket === opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <PackTimeseriesChart
            data={tsData}
            isLoading={tsLoading}
            title={`Downloads & Rank — ${tsBucket} bucket`}
            height={400}
            showRank
          />

          {tsData.length > 0 ? (
            <p className="text-xs text-glass-500 text-center tabular-nums">
              {tsData.length.toLocaleString()} data points
            </p>
          ) : !tsLoading ? (
            <p className="text-xs text-glass-500 text-center">
              Timeseries uses CurseForge download points (since migration 015); packs without
              CurseForge enrichment show an empty series until enrichment runs.
            </p>
          ) : null}
        </div>
      )}

      {/* ── Mod Lists Tab ────────────────────────────────────────────────── */}
      {activeTab === 'modlists' && (
        <div className="space-y-4">
          {mpiOnlyNote}
          {/* Diff tool */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="Modlist Diff Tool"
              icon={List}
              description="Compare two modlist states to see which mods were added or removed."
            />

            {/* Mode selector */}
            <div className="segmented mb-3" role="group" aria-label="Diff mode">
              <button
                type="button"
                onClick={() => setDiffMode('snapshot')}
                className="segmented-item"
                data-active={diffMode === 'snapshot' ? 'true' : 'false'}
                aria-pressed={diffMode === 'snapshot'}
              >
                By Snapshot
              </button>
              <button
                type="button"
                onClick={() => setDiffMode('date')}
                className="segmented-item"
                data-active={diffMode === 'date' ? 'true' : 'false'}
                aria-pressed={diffMode === 'date'}
              >
                By Date Range
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              {diffMode === 'snapshot' ? (
                <>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">From Snapshot</label>
                    <select
                      className="glass-select w-full"
                      value={diffFromId ?? ''}
                      onChange={(e) => setDiffFromId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Select...</option>
                      {modlists.map((ml) => (
                        <option key={ml.snapshot_id} value={ml.snapshot_id}>
                          #{ml.snapshot_id} — {format(new Date(ml.observed_at), 'MMM d, yyyy HH:mm')} ({ml.mod_ids.length} mods)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">To Snapshot</label>
                    <select
                      className="glass-select w-full"
                      value={diffToId ?? ''}
                      onChange={(e) => setDiffToId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Select...</option>
                      {modlists.map((ml) => (
                        <option key={ml.snapshot_id} value={ml.snapshot_id}>
                          #{ml.snapshot_id} — {format(new Date(ml.observed_at), 'MMM d, yyyy HH:mm')} ({ml.mod_ids.length} mods)
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">From Date</label>
                    <input
                      type="date"
                      className="glass-input w-full"
                      value={diffFromDate}
                      onChange={(e) => setDiffFromDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">To Date</label>
                    <input
                      type="date"
                      className="glass-input w-full"
                      value={diffToDate}
                      onChange={(e) => setDiffToDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              <button
                onClick={computeDiff}
                disabled={!canComputeDiff || diffLoading}
                className="btn-primary"
              >
                {diffLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Compare'}
              </button>
            </div>
            {diff && (
              <div className="mt-4 pt-4 border-t border-glass-200/10">
                <ModlistDiffViewer diff={diff} />
              </div>
            )}
          </div>

          {/* Modlist history */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader title="Modlist History" icon={List} count={modlistsLoading ? undefined : modlists.length} />
            {modlistsLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-12 w-full" />)}
              </div>
            ) : modlists.length === 0 ? (
              <EmptyState bare compact icon={List} title="No modlist data available" />
            ) : (
              <div className="divide-y divide-glass-200/10">
                {modlists.map((ml) => (
                  <div key={ml.snapshot_id}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 py-2.5 px-1 text-left rounded-md hover:bg-glass-200/5 transition-colors"
                      onClick={() => setExpandedModlist(expandedModlist === ml.snapshot_id ? null : ml.snapshot_id)}
                      aria-expanded={expandedModlist === ml.snapshot_id}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-wrap">
                        <span className="text-xs font-mono text-glass-400 tabular-nums">
                          #{ml.snapshot_id}
                        </span>
                        <span className="text-xs text-glass-300 tabular-nums">
                          {format(new Date(ml.observed_at), 'MMM d, yyyy HH:mm')}
                        </span>
                        <span className="text-xs text-glass-400 tabular-nums">
                          {ml.mod_ids.length} mods
                        </span>
                        {ml.cf_modlist_complete && (
                          <span className="badge badge-success">CF ✓</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-mono text-glass-500 truncate max-w-[120px]">{ml.modlist_hash}</span>
                        <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', expandedModlist === ml.snapshot_id && 'rotate-180')} />
                      </div>
                    </button>
                    {expandedModlist === ml.snapshot_id && (
                      <div className="subcard p-3 mb-2 space-y-2">
                        <div>
                          <p className="eyebrow mb-1.5">MPI Mod IDs ({ml.mod_ids.length})</p>
                          <div className="flex flex-wrap gap-1">
                            {ml.mod_ids.map((id) => (
                              <span key={id} className="chip font-mono tabular-nums">{id}</span>
                            ))}
                          </div>
                        </div>
                        {ml.cf_project_ids && ml.cf_project_ids.length > 0 && (
                          <div>
                            <p className="eyebrow mb-1.5">CF Project IDs ({ml.cf_project_ids.length})</p>
                            <div className="flex flex-wrap gap-1">
                              {ml.cf_project_ids.map((id) => (
                                <span key={id} className="chip font-mono tabular-nums bg-orange-500/10 text-orange-300 border-orange-500/20">{id}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {ml.cf_modlist_hash && (
                          <p className="text-xs text-glass-500">
                            CF Modlist Hash: <span className="font-mono">{ml.cf_modlist_hash}</span>
                          </p>
                        )}
                        <p className="text-xs text-glass-500">
                          Modlist Hash: <span className="font-mono">{ml.modlist_hash}</span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CurseForge Tab ───────────────────────────────────────────────── */}
      {activeTab === 'curseforge' && (
        <div className="space-y-6">
          {mpiOnlyNote}
          {/* Featured carousel history */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="CurseForge featured carousel"
              icon={Star}
              iconClassName="text-amber-400"
              description="Spans from Apogee featured-list crawls (project must map to a CurseForge mod id)."
              actions={
                cfFeaturedLatestCrawl ? (
                  <p className="text-xs text-glass-400 whitespace-nowrap">
                    Latest crawl{' '}
                    <span className="font-mono text-glass-300 tabular-nums">
                      {formatDistanceToNow(new Date(cfFeaturedLatestCrawl), { addSuffix: true })}
                    </span>
                  </p>
                ) : undefined
              }
            />
            {pack?.curseforge_featured?.currently_featured && (
              <div className="alert alert-warning mb-3">
                <Star />
                <span>
                  Currently on the CurseForge featured carousel
                  {pack.curseforge_featured.since && (
                    <span className="text-glass-400">
                      {' '}· since {formatDistanceToNow(new Date(pack.curseforge_featured.since), { addSuffix: true })}
                    </span>
                  )}
                </span>
              </div>
            )}
            {cfFeaturedLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} className="h-8 w-full" />)}
              </div>
            ) : cfFeaturedSpans.length === 0 ? (
              <p className="text-sm text-glass-400">No featured span history for this pack (mapping or crawls may be absent).</p>
            ) : (
              <div className="subcard overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Started</th>
                      <th className="th">Ended</th>
                      <th className="th">Last seen</th>
                      <th className="th text-right">CF mod</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cfFeaturedSpans.map((span, idx) => (
                      <tr key={`${span.started_at}-${span.cf_mod_id}-${idx}`}>
                        <td className="td text-xs font-mono text-glass-300 tabular-nums">
                          {format(new Date(span.started_at), 'MMM d, yyyy HH:mm')}
                        </td>
                        <td className="td text-xs text-glass-400 tabular-nums">
                          {span.ended_at ? format(new Date(span.ended_at), 'MMM d, yyyy HH:mm') : (
                            <span className="badge badge-amber">Open</span>
                          )}
                        </td>
                        <td className="td text-xs font-mono text-glass-400 tabular-nums">
                          {format(new Date(span.last_seen_at), 'MMM d, yyyy HH:mm')}
                        </td>
                        <td className="td text-xs font-mono text-glass-300 text-right tabular-nums">{span.cf_mod_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Files */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader title="CurseForge Files" icon={FileCode} count={cfFilesLoading ? undefined : cfFiles.length} />
            {cfFilesLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-10 w-full" />)}
              </div>
            ) : cfFiles.length === 0 ? (
              <EmptyState bare compact icon={FileCode} title="No CurseForge file data available" />
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block subcard overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="th">File Name</th>
                        <th className="th">Date</th>
                        <th className="th text-right">Downloads</th>
                        <th className="th">Type</th>
                        <th className="th">Observed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cfFiles.map((file) => {
                        const releaseType = file.release_type !== null ? CF_RELEASE_TYPES[file.release_type] : null;
                        return (
                          <tr key={file.cf_file_id}>
                            <td className="td text-white font-medium">{file.display_name}</td>
                            <td className="td text-xs text-glass-400 tabular-nums">
                              {file.file_date ? format(new Date(file.file_date), 'MMM d, yyyy') : '—'}
                            </td>
                            <td className="td text-glass-300 text-right tabular-nums">{formatNumber(file.download_count)}</td>
                            <td className="td">
                              {releaseType ? (
                                <span className={cn('badge', releaseType.color)}>
                                  {releaseType.label}
                                </span>
                              ) : (
                                <span className="text-xs text-glass-500">—</span>
                              )}
                            </td>
                            <td className="td text-xs text-glass-400 tabular-nums">
                              {formatDistanceToNow(new Date(file.observed_at), { addSuffix: true })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {cfFiles.map((file) => {
                    const releaseType = file.release_type !== null ? CF_RELEASE_TYPES[file.release_type] : null;
                    return (
                      <div key={file.cf_file_id} className="subcard p-3 space-y-1">
                        <p className="text-sm font-medium text-white">{file.display_name}</p>
                        <div className="flex items-center justify-between text-xs text-glass-400 tabular-nums">
                          <span>{file.file_date ? format(new Date(file.file_date), 'MMM d, yyyy') : '—'}</span>
                          <span>{formatNumber(file.download_count)} downloads</span>
                        </div>
                        {releaseType && (
                          <span className={cn('badge', releaseType.color)}>
                            {releaseType.label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Per-file download metric time series */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="Per-file download metrics"
              icon={BarChart3}
              description="Append-only observations from CurseForge enrichment (curseforge_file_metric_points). Newest rows first."
            />
            {cfMetricLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-8 w-full" />)}
              </div>
            ) : cfMetricPoints.length === 0 ? (
              <p className="text-sm text-glass-400">No file metric history yet (run CurseForge enrichment to populate).</p>
            ) : (
              <div className="subcard max-h-[28rem] overflow-y-auto overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Observed</th>
                      <th className="th text-right">File ID</th>
                      <th className="th text-right">Downloads</th>
                      <th className="th text-right">Mod ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cfMetricPoints.map((pt, idx) => (
                      <tr key={`${pt.cf_file_id}-${pt.observed_at}-${idx}`}>
                        <td className="td py-2 text-xs font-mono text-glass-300 tabular-nums whitespace-nowrap">
                          {format(new Date(pt.observed_at), 'MMM d, yyyy HH:mm')}
                        </td>
                        <td className="td py-2 text-xs font-mono text-glass-400 text-right tabular-nums">{pt.cf_file_id}</td>
                        <td className="td py-2 text-xs text-glass-300 text-right tabular-nums">{formatNumber(pt.download_count)}</td>
                        <td className="td py-2 text-xs font-mono text-glass-500 text-right tabular-nums">{pt.cf_mod_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Changelogs */}
          <div className="space-y-3">
            <SectionHeader
              title="Changelogs"
              icon={FileCode}
              count={changelogsLoading ? undefined : changelogs.length}
              className="mb-0"
              actions={
                <button
                  onClick={() => setChangelogsFull(!changelogsFull)}
                  className="glass-button-sm"
                >
                  {changelogsFull ? 'Show Excerpts' : 'Load Full Content'}
                </button>
              }
            />
            {changelogsLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} className="h-24 w-full" />)}
              </div>
            ) : (
              <ChangelogViewer changelogs={changelogs} />
            )}
          </div>
        </div>
      )}

      {/* ── External IDs Tab ─────────────────────────────────────────────── */}
      {activeTab === 'external' && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="External Platform IDs" icon={Globe} count={externalIdsLoading ? undefined : externalIds.length} />
          {mpiOnlyNote && <div className="mb-4">{mpiOnlyNote}</div>}
          {externalIdsLoading ? (
            <div className="space-y-2" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} className="h-12 w-full" />)}
            </div>
          ) : externalIds.length === 0 ? (
            <EmptyState bare compact icon={Globe} title="No external IDs mapped yet" />
          ) : (
            <div className="space-y-4">
              {/* Badge row */}
              <div className="flex flex-wrap gap-2">
                {externalIds.map((eid) => (
                  <ExternalIdBadge key={`${eid.source}-${eid.external_id}`} externalId={eid} packSlug={pack.slug} />
                ))}
              </div>

              {/* Detail table */}
              <div className="subcard overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Source</th>
                      <th className="th">External ID</th>
                      <th className="th">Confidence</th>
                      <th className="th">Forced</th>
                      <th className="th">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {externalIds.map((eid) => (
                      <tr key={`${eid.source}-${eid.external_id}`}>
                        <td className="td text-white font-medium capitalize">{eid.source}</td>
                        <td className="td font-mono text-glass-300 tabular-nums">{eid.external_id}</td>
                        <td className="td">
                          {eid.confidence !== null ? (
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 rounded-full bg-glass-200/10 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-accent-400"
                                  style={{ width: `${(eid.confidence * 100).toFixed(0)}%` }}
                                />
                              </div>
                              <span className="text-xs text-glass-400 tabular-nums">{(eid.confidence * 100).toFixed(0)}%</span>
                            </div>
                          ) : (
                            <span className="text-xs text-glass-500">—</span>
                          )}
                        </td>
                        <td className="td">
                          {eid.forced ? (
                            <span className="badge badge-amber">Yes</span>
                          ) : (
                            <span className="text-xs text-glass-500">No</span>
                          )}
                        </td>
                        <td className="td text-xs text-glass-400 tabular-nums">
                          {formatDistanceToNow(new Date(eid.updated_at), { addSuffix: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Integrations Tab ─────────────────────────────────────────────── */}
      {activeTab === 'integrations' && pack && (
        <PackCrossModuleSection
          packName={pack.name}
          packSlug={pack.slug}
          mpiId={effectiveMpiId}
          onCreatePartnership={() => setIsCreatePartnershipOpen(true)}
          onCreateModpackNote={() => setIsCreateModpackNoteOpen(true)}
        />
      )}

      {/* ── Snapshots Tab ────────────────────────────────────────────────── */}
      {activeTab === 'snapshots' && (
        <div className="space-y-4">
          {mpiOnlyNote}
          <SnapshotsTab
            snapshots={snapshots}
            loading={snapshotsLoading}
          />
        </div>
      )}

      {/* ── Cross-Module Modals ──────────────────────────────────────────── */}
      {isCreatePartnershipOpen && (
        <CreatePartnershipModal
          isOpen={isCreatePartnershipOpen}
          onClose={() => setIsCreatePartnershipOpen(false)}
          onSuccess={() => {
            setIsCreatePartnershipOpen(false);
            // Refresh integrations tab if active
            if (activeTab === 'integrations') {
              setActiveTab('overview');
              setTimeout(() => setActiveTab('integrations'), 50);
            }
          }}
          prefillPackName={pack?.name}
          prefillPackSummary={pack?.summary}
          prefillPackThumbnailUrl={pack?.thumbnail_url ?? undefined}
          prefillPlatformUrl={
            pack?.links?.curseforge ?? pack?.links?.modrinth ?? pack?.external_page_url ?? undefined
          }
          prefillAuthorCandidates={partnershipAuthorCandidates}
        />
      )}

      {isCreateModpackNoteOpen && (
        <CreateModpackNoteModal
          isOpen={isCreateModpackNoteOpen}
          onClose={() => setIsCreateModpackNoteOpen(false)}
          onSuccess={() => {
            setIsCreateModpackNoteOpen(false);
            // Refresh integrations tab if active
            if (activeTab === 'integrations') {
              setActiveTab('overview');
              setTimeout(() => setActiveTab('integrations'), 50);
            }
          }}
        />
      )}
    </div>
  );
};
